import React, { useMemo } from 'react';
import { RainbowAnimatedIcon, RainbowAnimatedText } from 'common/ui';
import { RequirementRecommendationCategory } from 'generated-graphql/types';
import { Link, Stack, Typography } from '@mui/material';
import { CheckOutlined } from '@mui/icons-material';

type Props = {
  recommendationsLoading: boolean;
  adjustedRequirementLoading: boolean;
  recommendationCompliant: boolean;
  requirementContent: string;
  originalRequirementContent?: string;
  adjustedRequirementExplanationText?: string;
  isRecommendationDismissed?: boolean | null;
  recommendationCategory?: RequirementRecommendationCategory | null;
  recommendationText?: string | null;
  onDismiss: () => void;
  onFix: (
    recommendationText: string,
    recommendationCategory: RequirementRecommendationCategory
  ) => void;
  onDelete: () => void;
  onUndo: () => void;
};

const recommendationHeader = {
  [RequirementRecommendationCategory.MultipleSkills]:
    'Avoid adding multiple skills',
  [RequirementRecommendationCategory.NeedsSpecificLocation]:
    'Use specific locations',
  [RequirementRecommendationCategory.NotAvailable]: 'Protected characteristic',
  [RequirementRecommendationCategory.OpenToInterpretation]:
    'Vague or subjective',
  [RequirementRecommendationCategory.TooRestrictive]: 'Too restrictive',
  [RequirementRecommendationCategory.VagueDateReference]: 'Use specific dates'
};

const props = {
  sx: { mb: 0.5 },
  component: 'button'
};

export const RequirementRecommendationView: React.FC<Props> = ({
  recommendationsLoading,
  adjustedRequirementLoading,
  recommendationCategory,
  isRecommendationDismissed,
  recommendationText,
  recommendationCompliant,
  requirementContent,
  originalRequirementContent,
  adjustedRequirementExplanationText,
  onDismiss,
  onFix,
  onDelete,
  onUndo
}) => {
  const recommendationActions = useMemo(() => {
    if (!recommendationCategory || !recommendationText) return [];

    const actions = [
      <Link {...props} key="dismiss" onClick={onDismiss}>
        Dismiss
      </Link>
    ];

    switch (recommendationCategory) {
      case RequirementRecommendationCategory.MultipleSkills:
      case RequirementRecommendationCategory.NeedsSpecificLocation:
      case RequirementRecommendationCategory.OpenToInterpretation:
      case RequirementRecommendationCategory.TooRestrictive:
      case RequirementRecommendationCategory.VagueDateReference: {
        actions.unshift(
          <Link
            {...props}
            key="edit"
            onClick={() => onFix(recommendationText, recommendationCategory)}
          >
            Fix requirement
          </Link>
        );
        break;
      }
      case RequirementRecommendationCategory.NotAvailable: {
        actions.unshift(
          <Link {...props} key="delete" onClick={onDelete}>
            Delete
          </Link>
        );
        break;
      }
    }
    return actions;
  }, [recommendationCategory, recommendationText]);

  if (recommendationsLoading) {
    return <RainbowAnimatedText>Analysing requirements</RainbowAnimatedText>;
  }

  if (adjustedRequirementLoading) {
    return <RainbowAnimatedText>Improving requirement</RainbowAnimatedText>;
  }

  if (recommendationCompliant) {
    return (
      <Stack direction="row" gap={1}>
        <CheckOutlined sx={{ color: 'success.dark', fontSize: '20px' }} />
        <Stack direction="column" alignItems="flex-start">
          <Typography fontWeight="bold">Looks good!</Typography>
          <Link onClick={onDismiss} fontWeight="normal" component="button">
            Dismiss
          </Link>
        </Stack>
      </Stack>
    );
  }

  if (
    originalRequirementContent &&
    adjustedRequirementExplanationText &&
    requirementContent !== originalRequirementContent
  ) {
    return (
      <Stack direction="row" gap={2}>
        <CheckOutlined sx={{ color: 'success.dark', fontSize: '20px' }} />
        <Stack direction="column" gap={1}>
          <Typography fontWeight="bold">
            {recommendationCategory ===
            RequirementRecommendationCategory.MultipleSkills
              ? 'Requirements separated'
              : 'Improvements made'}
          </Typography>
          <Typography>
            {adjustedRequirementExplanationText}{' '}
            <Link {...props} onClick={onUndo}>
              Undo
            </Link>
          </Typography>
        </Stack>
      </Stack>
    );
  }

  if (
    !isRecommendationDismissed &&
    recommendationCategory &&
    recommendationText
  ) {
    return (
      <Stack direction="column" gap={1}>
        <Typography fontWeight="bold">
          {recommendationHeader[recommendationCategory]}
        </Typography>
        <Stack direction="row" gap={2}>
          <RainbowAnimatedIcon />
          <Typography>
            {recommendationText}{' '}
            {recommendationActions.map((action, index) => (
              <React.Fragment key={index}>
                {action}
                {index < recommendationActions.length - 1 && ' or '}
              </React.Fragment>
            ))}
          </Typography>
        </Stack>
      </Stack>
    );
  }
  return null;
};
